import { useContext } from "react";
import { useIntl } from "react-intl";

import { LdoAuthContext } from "@nens/ldo/auth-v1";
import { Legend, Raster, legendSchema, rasterSchema } from "@nens/lizard/api-v4";
import { useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { API_BASE_URL } from "../constants";
import { Scenario } from "../domain/scenario";

export function useRasterTypeDescriptions(): Record<string, string> {
  const intl = useIntl();

  return {
    max_waterdepth: intl.formatMessage({
      id: "rastertype.max_waterdepth",
      defaultMessage: "Maximum water depth",
    }),
    max_waterlevel: intl.formatMessage({
      id: "rastertype.max_waterlevel",
      defaultMessage: "Maximum water level",
    }),
    max_velocity: intl.formatMessage({
      id: "rastertype.max_velocity",
      defaultMessage: "Maximum flow velocity",
    }),
    waterdepth_animation: intl.formatMessage({
      id: "rastertype.waterdepth_animation",
      defaultMessage: "Water depth animation",
    }),
    bathymetry: intl.formatMessage({
      id: "rastertype.bathymetry",
      defaultMessage: "Bathymetry",
    }),
    arrival_times: intl.formatMessage({
      id: "rastertype.arrival_times",
      defaultMessage: "Arrival times",
    }),
    rate_of_rise: intl.formatMessage({
      id: "rastertype.rate_of_rise",
      defaultMessage: "Rate of rise",
    }),
    rise_period: intl.formatMessage({
      id: "rastertype.rise_period",
      defaultMessage: "Rise period",
    }),
    inundation_sources: intl.formatMessage({
      id: "rastertype.inundation_sources",
      defaultMessage: "Inundation sources",
    }),
    total_damage: intl.formatMessage({
      id: "rastertype.total_damage",
      defaultMessage: "Total damage",
    }),
    total_victims: intl.formatMessage({
      id: "rastertype.total_victims",
      defaultMessage: "Total victims",
    }),
    total_affected: intl.formatMessage({
      id: "rastertype.total_affected",
      defaultMessage: "Total affected",
    }),
    mortality: intl.formatMessage({
      id: "rastertype.mortality",
      defaultMessage: "Mortality",
    }),
    flood_duration: intl.formatMessage({
      id: "rastertype.flood_duration",
      defaultMessage: "Flood duration",
    }),
  };
}

export function useScenarioRaster(scenario: Scenario | null, rasterType: string | null) {
  const raster = (scenario?.rasters ?? []).find(
    (raster) => rasterType !== null && raster.type === rasterType
  );
  const { apiFetchFunction } = useContext(LdoAuthContext);

  return useParsedQuery<Raster>(
    ["ldo", "scenario-raster", scenario?.id ?? -1, rasterType],
    () => {
      if (scenario === null) {
        // This should be diabled below
        throw new Error(`No scenario yet`);
      }
      if (rasterType === null) {
        // This should be disabled below
        throw new Error(`Raster type is not chosen yet`);
      }
      if (!raster) {
        throw new Error(`Scenario ${scenario.id} has no raster of type ${rasterType}.`);
      }

      const url = `${API_BASE_URL}/scenarios/${scenario.id}/rasters/${raster.id}`;
      return apiFetchFunction(url);
    },
    (json: unknown) => rasterSchema.parse(json),
    { enabled: scenario !== null && rasterType !== null }
  );
}

export function useScenarioRasters(scenario: Scenario) {
  // Fetch all rasters of a scenario
  const { apiFetchFunction } = useContext(LdoAuthContext);

  return useParsedQueries<{ type: string; raster: Raster }>(
    scenario.rasters.map((scenarioRaster) => {
      const { id, type } = scenarioRaster;
      return {
        queryKey: ["ldo", "scenario-raster", scenario.id, type],
        fetchFunction: () =>
          apiFetchFunction(`${API_BASE_URL}/scenarios/${scenario.id}/rasters/${id}`),
        parseFunction: (json: unknown) => ({
          type,
          raster: rasterSchema.parse(json),
        }),
      };
    })
  );
}

// Taken from Lizard api-v4, different base URL and different fetch function.
export function useWmsGetLegend(layer: string, style: string) {
  const { apiFetchFunction } = useContext(LdoAuthContext);
  const url = combineUrlAndParams("https://ldo.lizard.net/wms/", {
    service: "wms",
    request: "getlegend",
    layers: layer,
    style,
  });

  return useParsedQuery<Legend>(
    ["ldo", "scenario-raster-wms", url],
    () => apiFetchFunction(url),
    (json: unknown) => legendSchema.parse(json)
  );
}
